/**
 * Lightweight fertilizer plan model used by the live preview on
 * /tools/fertilizer-calculator. Self-contained — does not call into
 * the full `calculateFertilizerRecommendations` engine — so it can
 * re-run on every input change without an engine round-trip.
 *
 * The full engine still runs at submit time for the post-submit
 * detailed view (cost-per-bag, application schedule by month,
 * tip lists, etc.).
 */

export type FertGrassId =
  | "bermuda"
  | "zoysia"
  | "staug"
  | "centipede"
  | "bahia"
  | "fescue"
  | "kbg"
  | "rye"
  | "kikuyu"
  | "annualrye"
  | "coolmix"
  | "carpetgrass"

export type FertTypeId = "starter" | "balanced" | "highN" | "winterizer" | "organic"
export type FertSeasonId = "spring" | "summer" | "fall" | "winter"
export type FertGoalId = "maintain" | "green" | "thicken" | "newseed"
export type FertSoilId = "loam" | "sand" | "clay" | "silt" | "unknown"

export interface FertGrassDef {
  id: FertGrassId
  name: string
  /** Key used by `GRASS_CHARACTERISTICS` in `data/grass-characteristics.ts`. Must match exactly. */
  engineKey: string
  season: "warm" | "cool"
  tag: string
  /** Recommended lbs of nitrogen per 1,000 sq ft per year. */
  annualN: number
  /** 1-indexed months when this grass actively grows / accepts fertilizer. */
  primary: number[]
}

export const F_GRASSES: Record<FertGrassId, FertGrassDef> = {
  bermuda: {
    id: "bermuda",
    name: "Bermudagrass",
    engineKey: "Bermudagrass",
    season: "warm",
    tag: "Warm-season",
    annualN: 4.0,
    primary: [4, 5, 6, 7, 8, 9],
  },
  zoysia: {
    id: "zoysia",
    name: "Zoysia",
    engineKey: "Zoysiagrass",
    season: "warm",
    tag: "Warm-season",
    annualN: 3.0,
    primary: [4, 5, 6, 7, 8, 9],
  },
  staug: {
    id: "staug",
    name: "St. Augustine",
    engineKey: "St. Augustinegrass",
    season: "warm",
    tag: "Warm-season",
    annualN: 3.5,
    primary: [4, 5, 6, 7, 8, 9],
  },
  centipede: {
    id: "centipede",
    name: "Centipede",
    engineKey: "Centipede",
    season: "warm",
    tag: "Warm-season",
    annualN: 1.5,
    primary: [4, 5, 6, 7, 8],
  },
  bahia: {
    id: "bahia",
    name: "Bahiagrass",
    engineKey: "Bahiagrass",
    season: "warm",
    tag: "Warm-season",
    annualN: 2.0,
    primary: [4, 5, 6, 7, 8, 9],
  },
  fescue: {
    id: "fescue",
    name: "Tall fescue",
    engineKey: "Tall Fescue",
    season: "cool",
    tag: "Cool-season",
    annualN: 3.0,
    primary: [3, 4, 9, 10, 11],
  },
  kbg: {
    id: "kbg",
    name: "Kentucky bluegrass",
    engineKey: "Kentucky Bluegrass",
    season: "cool",
    tag: "Cool-season",
    annualN: 4.0,
    primary: [3, 4, 9, 10, 11],
  },
  rye: {
    id: "rye",
    name: "Ryegrass",
    engineKey: "Perennial Ryegrass",
    season: "cool",
    tag: "Cool-season",
    annualN: 3.0,
    primary: [3, 4, 9, 10, 11],
  },
  kikuyu: {
    id: "kikuyu",
    name: "Kikuyugrass",
    engineKey: "Kikuyugrass",
    season: "warm",
    tag: "Warm-season",
    // Aggressive spreader — light feeding only; overfeeding just accelerates it.
    annualN: 2.5,
    primary: [5, 6, 7, 8, 9],
  },
  annualrye: {
    id: "annualrye",
    name: "Annual ryegrass",
    engineKey: "Annual Ryegrass",
    season: "cool",
    tag: "Cool-season",
    // Winter overseed annual — light feeding during the cool season only.
    annualN: 1.5,
    primary: [2, 3, 10, 11, 12],
  },
  coolmix: {
    id: "coolmix",
    name: "Cool-season mix",
    engineKey: "Mixed Cool-Season Blend",
    season: "cool",
    tag: "Cool-season",
    // KBG/rye/fescue blend — feed like tall fescue, fall-weighted.
    annualN: 3.0,
    primary: [3, 4, 9, 10, 11],
  },
  carpetgrass: {
    id: "carpetgrass",
    name: "Carpetgrass",
    engineKey: "Carpetgrass",
    season: "warm",
    tag: "Warm-season",
    // LSU AgCenter recommends no more than 1 lb N/1,000 sq ft per year,
    // applied from mid-spring through mid-summer.
    annualN: 1.0,
    primary: [4, 5, 6, 7],
  },
}

/** Maps form's user-facing grass-name strings to our short ids. */
export const FERT_GRASS_NAME_TO_ID: Record<string, FertGrassId> = {
  Bermudagrass: "bermuda",
  Bermuda: "bermuda",
  Zoysiagrass: "zoysia",
  Zoysia: "zoysia",
  "St. Augustinegrass": "staug",
  "St. Augustine": "staug",
  Centipede: "centipede",
  Bahiagrass: "bahia",
  "Tall Fescue": "fescue",
  "Tall fescue": "fescue",
  "Kentucky Bluegrass": "kbg",
  "Kentucky bluegrass": "kbg",
  "Perennial Ryegrass": "rye",
  Ryegrass: "rye",
  Kikuyugrass: "kikuyu",
  Kikuyu: "kikuyu",
  "Annual Ryegrass": "annualrye",
  "Annual ryegrass": "annualrye",
  "Mixed Cool-Season Blend": "coolmix",
  "Cool-season mix": "coolmix",
  Carpetgrass: "carpetgrass",
  "Carpet grass": "carpetgrass",
}

export interface FertTypeDef {
  id: FertTypeId
  name: string
  npk: string
  blurb: string
  mix: { n: number; p: number; k: number }
  /** Engine key used at submit time for the legacy result view. */
  engineKey: string
}

export const F_TYPES: Record<FertTypeId, FertTypeDef> = {
  starter: {
    id: "starter",
    name: "Starter",
    npk: "20-27-5",
    blurb: "High phosphorus for new seed.",
    mix: { n: 0.2, p: 0.27, k: 0.05 },
    engineKey: "starter-18-24-12",
  },
  balanced: {
    id: "balanced",
    name: "Balanced",
    npk: "10-10-10",
    blurb: "Even split. General-purpose.",
    mix: { n: 0.1, p: 0.1, k: 0.1 },
    engineKey: "balanced-10-10-10",
  },
  highN: {
    id: "highN",
    name: "High N",
    npk: "32-0-4",
    blurb: "Push green growth.",
    mix: { n: 0.32, p: 0.0, k: 0.04 },
    engineKey: "high-nitrogen-32-0-4",
  },
  winterizer: {
    id: "winterizer",
    name: "Winterizer",
    npk: "24-0-12",
    blurb: "Roots before dormancy.",
    mix: { n: 0.24, p: 0.0, k: 0.12 },
    engineKey: "winterizer-22-0-8",
  },
  organic: {
    id: "organic",
    name: "Organic",
    npk: "6-2-0",
    blurb: "Slow-release. Soil-friendly.",
    mix: { n: 0.06, p: 0.02, k: 0.0 },
    engineKey: "organic-6-2-0",
  },
}

export interface FertSeasonDef {
  id: FertSeasonId
  label: string
  factor: number
}

export const F_SEASONS: FertSeasonDef[] = [
  { id: "spring", label: "Spring", factor: 1.0 },
  { id: "summer", label: "Summer", factor: 0.6 },
  { id: "fall", label: "Fall", factor: 1.2 },
  { id: "winter", label: "Winter", factor: 0.0 },
]

export type FertEngineGoal = "maintenance" | "recovery" | "establishment" | "seasonal"

export interface FertGoalDef {
  id: FertGoalId
  name: string
  blurb: string
  factor: number
  engineKey: FertEngineGoal
}

export const F_GOALS: Record<FertGoalId, FertGoalDef> = {
  maintain: {
    id: "maintain",
    name: "Maintain",
    blurb: "Keep what I have, healthy.",
    factor: 1.0,
    engineKey: "maintenance",
  },
  green: {
    id: "green",
    name: "Greener",
    blurb: "Push deeper color.",
    factor: 1.15,
    engineKey: "seasonal",
  },
  thicken: {
    id: "thicken",
    name: "Thicker",
    blurb: "Fill in thin spots.",
    factor: 1.25,
    engineKey: "recovery",
  },
  newseed: {
    id: "newseed",
    name: "Just seeded",
    blurb: "Establish new grass.",
    factor: 1.4,
    engineKey: "establishment",
  },
}

export interface FertSoilDef {
  id: FertSoilId
  name: string
  blurb: string
  mod: number
  /** Engine key used at submit time. Engine accepts `clay | sandy | loamy | unknown`. */
  engineKey: "clay" | "sandy" | "loamy" | "unknown"
}

export const F_SOILS: Record<FertSoilId, FertSoilDef> = {
  loam: {
    id: "loam",
    name: "Loamy",
    blurb: "Holds nutrients well. Standard rate.",
    mod: 1.0,
    engineKey: "loamy",
  },
  sand: {
    id: "sand",
    name: "Sandy",
    blurb: "Leaches fast. Split into more applications.",
    mod: 1.1,
    engineKey: "sandy",
  },
  clay: {
    id: "clay",
    name: "Clay",
    blurb: "Slow uptake. Slightly lighter rate.",
    mod: 0.9,
    engineKey: "clay",
  },
  silt: {
    id: "silt",
    name: "Silty",
    blurb: "Holds moisture. Standard rate.",
    mod: 1.0,
    engineKey: "loamy", // engine doesn't model silt; closest behavior is loamy
  },
  unknown: {
    id: "unknown",
    name: "Not sure",
    blurb: "We'll use the standard rate.",
    mod: 1.0,
    engineKey: "unknown",
  },
}

export const F_MONTH_LABEL = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export interface FertMonthCell {
  i: number
  label: string
  on: boolean
}

export interface FertilizerPlan {
  grass: FertGrassDef
  type: FertTypeDef
  season: FertSeasonDef
  goal: FertGoalDef
  soil: FertSoilDef
  /** Annual lbs of N for the whole lawn. */
  annualNlbs: number
  /** N lbs delivered by THIS application. */
  thisAppNlbs: number
  /** Total lbs of product to buy for this application. */
  productLbs: number
  /** Bags at standard 40-lb bag size. */
  bags: number
  bagSize: number
  /** Estimated total applications per year. */
  appsPerYear: number
  /** 12-month on/off table for the chosen grass. */
  months: FertMonthCell[]
  /** Lbs of N / P / K delivered by this application. */
  npk: { n: number; p: number; k: number }
}

interface BuildPlanInput {
  sqft: number
  grass: FertGrassId
  type: FertTypeId
  season: FertSeasonId
  goal: FertGoalId
  soil: FertSoilId
}

export function buildFertilizerPlan({
  sqft,
  grass,
  type,
  season,
  goal,
  soil,
}: BuildPlanInput): FertilizerPlan {
  const g = F_GRASSES[grass] ?? F_GRASSES.bermuda
  const t = F_TYPES[type] ?? F_TYPES.balanced
  const s = F_SEASONS.find(x => x.id === season) ?? F_SEASONS[0]
  const go = F_GOALS[goal] ?? F_GOALS.maintain
  const so = F_SOILS[soil] ?? F_SOILS.loam

  const k1 = sqft / 1000
  const isCarpetgrass = g.id === "carpetgrass"
  // Carpetgrass has a sourced annual ceiling of 1 lb N / 1,000 sq ft. Keep
  // the goal and soil adjustments from raising that ceiling. Other grasses
  // retain the historical goal/soil math below.
  const annualNPer1000 = isCarpetgrass
    ? Math.min(g.annualN, g.annualN * go.factor * so.mod)
    : g.annualN * go.factor * so.mod
  const annualNlbs = annualNPer1000 * k1
  const requestedAppNlbs = isCarpetgrass
    ? (s.id === "spring" || s.id === "summer"
      ? Math.min(0.5, 1.0 * s.factor * go.factor * so.mod) * k1
      : 0)
    : Math.max(0, 1.0 * s.factor * go.factor * k1)
  const appsPerYear = Math.max(2, Math.round(annualNlbs / Math.max(0.5, requestedAppNlbs)))
  // Only Carpetgrass uses the annual and per-application cap. Existing grasses
  // keep their previous requested application output.
  const thisAppNlbs = isCarpetgrass
    ? Math.min(requestedAppNlbs, annualNlbs / appsPerYear)
    : requestedAppNlbs
  const productLbs = t.mix.n > 0 ? thisAppNlbs / t.mix.n : thisAppNlbs
  const bagSize = 40
  const bags = productLbs / bagSize

  const months = F_MONTH_LABEL.map((label, i) => {
    const m = i + 1
    return { i, label, on: g.primary.includes(m) }
  })

  const npk = {
    n: productLbs * t.mix.n,
    p: productLbs * t.mix.p,
    k: productLbs * t.mix.k,
  }

  return {
    grass: g,
    type: t,
    season: s,
    goal: go,
    soil: so,
    annualNlbs,
    thisAppNlbs,
    productLbs,
    bags,
    bagSize,
    appsPerYear,
    months,
    npk,
  }
}
